/**
 * Tool Mappings
 *
 * This file provides unified mappings for tool IDs, display names, colors, and icons.
 * Uses the centralized tool definitions from sprites-types for consistency.
 *
 * When adding new tools:
 * 1. Add them to TOOL_DEFINITIONS in sprites-types/workflow/tools.ts
 * 2. The mappings will be automatically generated from the unified definitions
 */

import {
  FaBrain,
  FaBullseye,
  FaCheckCircle,
  FaCodeBranch,
  FaCog,
  FaComment,
  FaEdit,
  FaEnvelope,
  FaFileAlt,
  FaFilter,
  FaFlag,
  FaGlobe,
  FaLayerGroup,
  FaLinkedin,
  FaPalette,
  FaPlay,
  FaReddit,
  FaRobot,
  FaRoute,
  FaSearch,
  FaSquare,
  FaTable,
  FaTwitter,
  FaUpload,
  FaWordpress,
  FaYoutube,
} from 'react-icons/fa'
import { SiFramer, SiGoogleadsense, SiMeta, SiShopify, SiWebflow } from 'react-icons/si'
import {
  TOOL_DEFINITIONS,
  ToolIcon,
  WorkflowToolIds,
  ToolMetadata as WorkflowToolMetadata,
  getAllIcons,
  isValidToolId,
} from 'sprites-types/workflow/tools'

export interface WorkflowToolMapping {
  id: string
  displayName: string
  description?: string
  category: 'brand' | 'generic'
  color: string
  backgroundColor: string
  icon: React.ComponentType<any>
}

// Icon mapping for tool icons (maps icon identifiers to React components)
// This must match the ToolIconSchema in sprites-types
const ICON_MAP: Record<ToolIcon, React.ComponentType<any>> = {
  // Ads
  google_ads: SiGoogleadsense,

  // Search & Web
  search: FaSearch,
  browser: FaGlobe,

  // Communication & Social
  gmail: FaEnvelope,
  google_drive: FaGlobe,
  linkedin: FaLinkedin,
  meta: SiMeta,
  reddit: FaReddit,
  twitter: FaTwitter,
  youtube: FaYoutube,

  // Native/System
  approval: FaCheckCircle,
  upload: FaUpload,
  batch: FaLayerGroup,
  filter: FaFilter,
  flatten: FaSquare,
  merge: FaCodeBranch,
  route: FaRoute,
  target: FaBullseye,

  // CSV & Data
  csv: FaTable,
  'ai-csv': FaBrain,

  // AI/LLM
  'image-gen': FaPalette,
  chat: FaComment,
  'text-gen': FaRobot,
  'text-edit': FaEdit,

  // WordPress
  wordpress: FaWordpress,

  // Framer
  framer: SiFramer,

  // Webflow
  webflow: SiWebflow,

  // Shopify
  shopify: SiShopify,

  // Fallbacks
  default: FaCog,
}

// Helper function to create tool mapping from ToolMetadata
const createWorkflowToolMapping = (tool: WorkflowToolMetadata): WorkflowToolMapping => ({
  id: tool.id,
  displayName: tool.displayName,
  description: tool.displayDescription,
  category: tool.category === 'social' || tool.category === 'communication' ? 'brand' : 'generic',
  color: tool.color,
  backgroundColor: tool.backgroundColor,
  icon: ICON_MAP[tool.icon],
})

// Generate all tool mappings from unified definitions
export const TOOL_MAPPINGS: Record<WorkflowToolIds, WorkflowToolMapping> = Object.fromEntries(
  Object.values(TOOL_DEFINITIONS).map((tool) => [tool.id, createWorkflowToolMapping(tool)])
) as Record<WorkflowToolIds, WorkflowToolMapping>

// Reverse mapping (display name to tool ID)
export const DISPLAY_TO_TOOL_ID: Record<string, WorkflowToolIds> = Object.entries(
  TOOL_MAPPINGS
).reduce(
  (acc, [toolId, mapping]) => {
    acc[mapping.displayName] = toolId as WorkflowToolIds
    return acc
  },
  {} as Record<string, WorkflowToolIds>
)

// Helper functions
export const getToolMapping = (toolId: string): WorkflowToolMapping | null => {
  if (isValidToolId(toolId)) {
    return TOOL_MAPPINGS[toolId as WorkflowToolIds]
  }
  return null
}

export const getDisplayName = (toolId: string): string => {
  const mapping = getToolMapping(toolId)
  return mapping?.displayName || toolId
}

export const getToolId = (displayName: string): string => {
  return DISPLAY_TO_TOOL_ID[displayName] || displayName
}

export const getToolColor = (toolId: string): { color: string; backgroundColor: string } => {
  if (toolId === 'start') {
    return { color: '#10b981', backgroundColor: '#10b98115' }
  } else if (toolId === 'end') {
    return { color: '#ef4444', backgroundColor: '#ef444415' }
  }

  const mapping = getToolMapping(toolId)
  return mapping
    ? { color: mapping.color, backgroundColor: mapping.backgroundColor }
    : { color: '#6B7280', backgroundColor: '#6B728015' }
}

export const getToolIcon = (toolId: string): React.ComponentType<any> => {
  if (toolId === 'start') {
    return FaPlay
  } else if (toolId === 'end') {
    return FaFlag
  }

  const mapping = getToolMapping(toolId)

  return mapping?.icon || FaFileAlt
}

export const getAllToolIds = (): WorkflowToolIds[] => {
  return Object.keys(TOOL_MAPPINGS) as WorkflowToolIds[]
}

export const getAllToolMappings = (): WorkflowToolMapping[] => {
  return Object.values(TOOL_MAPPINGS)
}

export const getAllDisplayNames = (): string[] => {
  return Object.values(TOOL_MAPPINGS).map((mapping) => mapping.displayName)
}

// Check if a tool ID is valid
export const isToolId = (toolId: string): toolId is WorkflowToolIds => {
  return isValidToolId(toolId)
}

// Check if a display name is valid
export const isDisplayName = (displayName: string): boolean => {
  return displayName in DISPLAY_TO_TOOL_ID
}

// Get tools by category
export const getToolsByCategory = (category: 'brand' | 'generic'): WorkflowToolMapping[] => {
  return Object.values(TOOL_MAPPINGS).filter((mapping) => mapping.category === category)
}

export const getBrandTools = () => getToolsByCategory('brand')
export const getGenericTools = () => getToolsByCategory('generic')

// Get all unique colors used
export const getUsedColors = (): string[] => {
  return [...new Set(Object.values(TOOL_MAPPINGS).map((mapping) => mapping.color))]
}

// Get available icons for reference
export const getAvailableIcons = (): ToolIcon[] => {
  return getAllIcons()
}

// Legacy compatibility functions (for smooth migration)
export const getSkillColor = (skillName: string) => {
  const mapping = getToolMapping(skillName)
  if (!mapping) {
    console.warn(`No mapping found for skill: "${skillName}". Using default.`)
    return {
      color: '#6B7280',
      backgroundColor: '#6B728015',
      icon: FaCog,
      category: 'generic' as const,
    }
  }

  return {
    color: mapping.color,
    backgroundColor: mapping.backgroundColor,
    icon: mapping.icon,
    category: mapping.category,
  }
}

export const getCanonicalName = getToolId
export const isCanonicalName = isToolId
